import './App.css';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { setTenantPortalSession } from './tenantPortalSession';
import { setStaffPortalSession } from './staffPortalSession';

const ADMIN_LOGO_URL =
  'https://ogtwmrdpuaofpcmpeguz.supabase.co/storage/v1/object/public/logo/WhatsApp_Image_2026-04-12_at_4.48.39_PM-removebg-preview%20(1).png';

/** Landlord / Admin login on /admin. Override in `.env.local` for production. */
const LANDLORD_ADMIN_EMAIL = (process.env.REACT_APP_LANDLORD_ADMIN_EMAIL || 'admin@odus.com')
  .trim()
  .toLowerCase();
const LANDLORD_ADMIN_PASSWORD = process.env.REACT_APP_LANDLORD_ADMIN_PASSWORD || 'OdusAdmin2026';

function parseRpcResult(data) {
  if (data == null) return null;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function App() {
  const [role, setRole] = React.useState('tenant');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loginError, setLoginError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (role === 'tenant') {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        setLoginError('Enter the email and password you use for the tenant portal.');
        return;
      }
      if (!supabase) {
        setLoginError('Supabase is not configured.');
        return;
      }

      setSubmitting(true);
      try {
        const { data, error: rpcError } = await supabase.rpc('login_prospective_tenant', {
          p_email: trimmedEmail,
          p_password: password,
        });
        if (rpcError) throw rpcError;
        const row = parseRpcResult(data);
        if (!row?.ok) {
          setLoginError(row?.error || 'Invalid email or password.');
          return;
        }
        setTenantPortalSession({
          tenantId: row.tenant_id,
          email: row.email || trimmedEmail,
          fullName: row.full_name || '',
        });
        navigate('/tenant');
      } catch (err) {
        setLoginError(
          err?.message ||
            'Could not sign in. Run sql/add_prospective_tenant_password_supabase.sql in Supabase (includes login_prospective_tenant).'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (role === 'landlord') {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail || !password) {
        setLoginError('Enter the admin email and password for Landlord / Admin.');
        return;
      }
      if (trimmedEmail !== LANDLORD_ADMIN_EMAIL || password !== LANDLORD_ADMIN_PASSWORD) {
        setLoginError('Invalid email or password.');
        return;
      }
      navigate('/dashboard');
      return;
    }

    if (role === 'maintenance') {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        setLoginError('Enter the email and password set for this staff member on Staff → Add / Edit.');
        return;
      }
      if (!supabase) {
        setLoginError('Supabase is not configured.');
        return;
      }
      setSubmitting(true);
      try {
        const { data, error: rpcError } = await supabase.rpc('login_maintenance_staff', {
          p_email: trimmedEmail,
          p_password: password,
        });
        if (rpcError) throw rpcError;
        const row = parseRpcResult(data);
        if (!row?.ok) {
          setLoginError(row?.error || 'Invalid email or password.');
          return;
        }
        setStaffPortalSession({
          staffId: row.staff_id,
          email: row.email || trimmedEmail,
          fullName: row.full_name || '',
          role: row.role || '',
        });
        navigate('/staff-dashboard');
      } catch (err) {
        setLoginError(
          err?.message ||
            'Could not sign in. Run sql/add_maintenance_staff_password_supabase.sql in Supabase (includes login_maintenance_staff).'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    navigate('/dashboard');
  };

  return (
    <div className="App">
      <main className="auth-shell">
        <section className="auth-card" aria-label="Authentication">
          <div className="auth-card-inner">
            <header className="brand">
              <div className="brand-logo">
                <img
                  src={ADMIN_LOGO_URL}
                  alt="ODUS"
                  className="brand-logo-img"
                  width={200}
                  height={88}
                  decoding="async"
                />
              </div>
              <div>
                <h1 className="brand-title">ODUS</h1>
                <p className="brand-subtitle">
                  Sign in to manage properties, tenants, and maintenance.
                </p>
              </div>
            </header>

            <form className="form-block" onSubmit={handleSubmit} noValidate>
              {loginError ? (
                <p className="properties-save-error" role="alert">
                  {loginError}
                </p>
              ) : null}

              <div className="form-fields">
                <div className="field-wrapper">
                  <label className="field-label" htmlFor="email">
                    Email
                  </label>
                  <div className="input-shell">
                    <div className="input-icon">@</div>
                    <input
                      id="email"
                      className="input-element"
                      type="text"
                      placeholder={
                        role === 'tenant'
                          ? 'Same email as on Add prospective tenant'
                          : role === 'maintenance'
                            ? 'Same email as on Staff → Add / Edit staff'
                            : LANDLORD_ADMIN_EMAIL
                      }
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="field-wrapper">
                  <label className="field-label" htmlFor="password">
                    Password
                  </label>
                  <div className="input-shell">
                    <div className="input-icon">•••</div>
                    <input
                      id="password"
                      className="input-element"
                      type="password"
                      placeholder={
                        role === 'tenant'
                          ? 'Same password as set for that tenant'
                          : role === 'maintenance'
                            ? 'Password set when adding or editing this staff member'
                            : 'Your admin password'
                      }
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="field-wrapper">
                  <label className="field-label" htmlFor="role">
                    Role
                  </label>
                  <div className="input-shell">
                    <div className="input-icon" role="img" aria-label="role">
                      🏷
                    </div>
                    <select
                      id="role"
                      className="input-element"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      <option value="tenant">Tenant</option>
                      <option value="landlord">Landlord / Admin</option>
                      <option value="maintenance">Maintenance Staff</option>
                    </select>
                    <span className="select-arrow">▾</span>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <label className="remember-me">
                  <input type="checkbox" defaultChecked />
                  <span>Keep me signed in</span>
                </label>
              </div>

              <div className="actions">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Login'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
